import React, { useEffect, useMemo, useRef } from 'react';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';

let isZoomPluginRegistered = false;
if (!isZoomPluginRegistered) {
  try {
    Chart.register(zoomPlugin);
  } catch (_err) {
    // Ignore duplicate registration / HMR edge cases.
  }
  isZoomPluginRegistered = true;
}

function defaultGetX(p) {
  return p?.date ?? null;
}

export default function TimeSeriesChart({
  series = [],
  getX = defaultGetX,
  height = 280,
  datasets = [],
  onClickPoint = null,
  options = {},
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const labels = useMemo(() => series.map((p) => getX(p)).filter(Boolean), [series, getX]);

  const chartDatasets = useMemo(() => {
    return datasets.map((d) => {
      const data = series.map((p) => {
        const v = typeof d.getY === 'function' ? d.getY(p) : null;
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      });
      const pointRadius =
        typeof d.getPointRadius === 'function' ? series.map((p) => d.getPointRadius(p)) : (d.pointRadius ?? 0);
      const pointHoverRadius =
        typeof d.getPointHoverRadius === 'function'
          ? series.map((p) => d.getPointHoverRadius(p))
          : (d.pointHoverRadius ?? 0);

      return {
        label: d.label,
        data,
        hidden: !!d.hidden,
        yAxisID: d.yAxisID ?? 'y',
        borderColor: d.borderColor,
        backgroundColor: d.backgroundColor ?? 'transparent',
        borderWidth: typeof d.borderWidth === 'number' ? d.borderWidth : 2,
        tension: typeof d.tension === 'number' ? d.tension : 0.25,
        fill: !!d.fill,
        showLine: d.showLine !== false,
        pointRadius,
        pointHoverRadius,
        pointHitRadius: typeof d.pointHitRadius === 'number' ? d.pointHitRadius : 8,
        pointBackgroundColor: d.pointBackgroundColor,
        pointBorderColor: d.pointBorderColor,
        pointBorderWidth: d.pointBorderWidth,
        borderDash: d.borderDash,
      };
    });
  }, [datasets, series]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!Array.isArray(series) || series.length === 0) return;

    const ctx = canvas.getContext('2d');
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: chartDatasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        onClick: (evt, elements, chart) => {
          if (typeof onClickPoint !== 'function') return;
          if (!Array.isArray(elements) || elements.length === 0) return;
          const first = elements[0];
          const dataset = chart?.data?.datasets?.[first.datasetIndex];
          const label = dataset?.label;
          const index = first.index;
          const point = series?.[index];
          onClickPoint({ label, index, point });
        },
        plugins: {
          legend: { labels: { color: '#aaa' } },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
          zoom: {
            pan: { enabled: true, mode: 'x' },
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' },
          },
        },
        scales: {
          x: {
            ticks: { color: '#888', maxTicksLimit: 8 },
            grid: { color: 'rgba(255,255,255,0.06)' },
          },
          y: {
            ticks: { color: '#888' },
            grid: { color: 'rgba(255,255,255,0.06)' },
          },
        },
        ...options,
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [labels, chartDatasets, onClickPoint, options, series]);

  return (
    <div style={{ height }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

